import mixpanel from "mixpanel-browser";

// URL to Screen Name Mapping for Standalone Checkout App
export const URL_TO_SCREEN: Record<string, string> = {
  // Active URLs
  "/": "Introduction",
  "/signin": "Sign In",
  "/otp": "OTP Authentication",
  "/pay": "Payment Selection",
  "/prepay": "Pre-Payment Selection", // Not currently used in flow
  "/pending": "Payment Pending",
  "/classic": "Classic Flow",
  "/repay": "Repayment Selection", // Not currently used in flow
  "/payment-confirm": "Payment Confirmation",
  "/success": "Payment Success",
  "/error": "Payment Error",
  "/terms": "Terms and Conditions",
  "/help": "Help and FAQ",
  "/redirect": "Redirect to Merchant",
  "/healthz": "Health Check",
  "/session-timeout": "Session Timeout",

  // Error screens with specific causes
  "/error?cause=request": "Payment Failure (Invalid Request)",
  "/error?cause=rejected": "Payment Failure (Rejected)",
  "/error?cause=timeout": "Payment Failure (Timeout)",
  "/error?cause=ekyc-rejected": "Payment Failure (eKYC Rejected)",
  "/error?cause=hokatsu-rejected": "Payment Failure (Hokatsu Rejected)",
};

// Additional event properties per URL
export const URL_TO_EXTRAS: Record<string, Record<string, string>> = {
  "/success": { "Event Type": "Payment Authorized" },
  "/error": { "Event Type": "Payment Failed" },
};

// Action Names
export const MIXPANEL_ACTION_APP_OPEN = "App Open";
export const MIXPANEL_ACTION_APP_CLOSE = "App Close";
export const MIXPANEL_ACTION_BUTTON_CLICK = "Button Click";
export const MIXPANEL_ACTION_CLICK = "Click";
export const MIXPANEL_ACTION_PAGE_VIEW = "Page View";
export const MIXPANEL_ACTION_SCROLL = "Scroll";
export const MIXPANEL_ACTION_FIELD_EDIT = "Field Edit";
export const MIXPANEL_ACTION_LINK_CLICK = "Link Click";
export const MIXPANEL_ACTION_FORM_ERROR = "Form Error";
export const MIXPANEL_ACTION_AUTHORIZATION_SUCCESS = "Authentication Success";
export const MIXPANEL_ACTION_AUTHORIZATION_FAILURE = "Authentication Failure";
export const MIXPANEL_ACTION_API_ERROR = "API Error";
export const MIXPANEL_ACTION_PRE_CALLBACK = "Pre Callback";
export const MIXPANEL_ACTION_CALLBACK = "Callback";

// Common Actions
export const MIXPANEL_ACTION_SUBMIT = "MIXPANEL_ACTION_SUBMIT";
export const MIXPANEL_ACTION_BACK = "MIXPANEL_ACTION_BACK";
export const MIXPANEL_ACTION_INSTALLMENT_SELECT = "MIXPANEL_ACTION_INSTALLMENT_SELECT";
export const MIXPANEL_ACTION_NEXT = "MIXPANEL_ACTION_NEXT";
export const MIXPANEL_ACTION_CONFIRM = "MIXPANEL_ACTION_CONFIRM";
export const MIXPANEL_ACTION_HELP = "MIXPANEL_ACTION_HELP";

// Action Items
export const MIXPANEL_ITEM_SUBMIT = "Submit";
export const MIXPANEL_ITEM_BACK = "Back";
export const MIXPANEL_ITEM_INSTALLMENT_OPTION = "Installment Option";
export const MIXPANEL_ITEM_NEXT = "Next";
export const MIXPANEL_ITEM_CONFIRM = "Confirm";
export const MIXPANEL_ITEM_HELP = "Help Content";

// Common action sets mapping
export const COMMON_ACTION_SETS: Record<string, { actionName: string; extra: Record<string, string> }> = {
  [MIXPANEL_ACTION_SUBMIT]: {
    actionName: MIXPANEL_ACTION_BUTTON_CLICK,
    extra: {
      item: MIXPANEL_ITEM_SUBMIT,
    },
  },
  [MIXPANEL_ACTION_BACK]: {
    actionName: MIXPANEL_ACTION_BUTTON_CLICK,
    extra: {
      item: MIXPANEL_ITEM_BACK,
    },
  },
  [MIXPANEL_ACTION_INSTALLMENT_SELECT]: {
    actionName: MIXPANEL_ACTION_BUTTON_CLICK,
    extra: {
      item: MIXPANEL_ITEM_INSTALLMENT_OPTION,
    },
  },
  [MIXPANEL_ACTION_NEXT]: {
    actionName: MIXPANEL_ACTION_BUTTON_CLICK,
    extra: {
      item: MIXPANEL_ITEM_NEXT,
    },
  },
  [MIXPANEL_ACTION_CONFIRM]: {
    actionName: MIXPANEL_ACTION_BUTTON_CLICK,
    extra: {
      item: MIXPANEL_ITEM_CONFIRM,
    },
  },
  [MIXPANEL_ACTION_HELP]: {
    actionName: MIXPANEL_ACTION_BUTTON_CLICK,
    extra: {
      item: MIXPANEL_ITEM_HELP,
    },
  },
};

const getScreenName = (pathname: string): string | undefined => URL_TO_SCREEN[pathname];
const getScreenExtra = (pathname: string): Record<string, string> | undefined => URL_TO_EXTRAS[pathname];

interface TrackActionParams {
  pathname: string;
  customPath?: string;
  actionName: string;
  actionItem?: string;
  extraData?: Record<string, unknown>;
}

interface TrackDurationParams extends TrackActionParams {
  shouldEndTracker?: boolean;
}

export class MixpanelHelpers {
  private static sessionData: Record<string, unknown> = {};
  private static isInitialized = false;
  private static initPromise: Promise<void> | null = null;
  private static eventQueue: Array<() => void> = [];
  private static sessionId = "";

  // Initialize mixpanel (idempotent - safe to call multiple times)
  static initialize(): Promise<void> {
    if (MixpanelHelpers.isInitialized) {
      return Promise.resolve();
    }

    if (MixpanelHelpers.initPromise) {
      return MixpanelHelpers.initPromise;
    }

    // Get config from server-injected global variable (preferred) or fallback to process.env
    const mixpanelConfig = typeof window !== "undefined" && (window as any).__MIXPANEL_CONFIG__;
    const mixpanelToken = mixpanelConfig?.token || process.env.MIXPANEL_TOKEN;
    const enableDebug = mixpanelConfig?.debug ?? false;

    // Store sessionId from config
    if (mixpanelConfig?.sessionId) {
      MixpanelHelpers.sessionId = mixpanelConfig.sessionId;
    }

    if (!mixpanelToken) {
      MixpanelHelpers.isInitialized = true; // Mark as initialized to prevent retries
      return Promise.resolve();
    }

    MixpanelHelpers.initPromise = new Promise<void>((resolve) => {
      mixpanel.init(mixpanelToken, {
        persistence: "localStorage",
        loaded(mp) {
          if (enableDebug) {
            mp.set_config({ debug: true });
          }

          // Register super properties after mixpanel is fully loaded
          mp.register({
            Product: "Checkout Standalone",
            Platform: "Web",
          });

          MixpanelHelpers.isInitialized = true;

          // Flush queued events
          MixpanelHelpers.eventQueue.forEach((fn) => fn());
          MixpanelHelpers.eventQueue = [];

          resolve();
        },
      });
    });

    return MixpanelHelpers.initPromise;
  }

  // Remove super properties
  static unregisterSuperProperties(properties: string[] = []): void {
    properties.forEach((property) => {
      mixpanel.unregister(property);
    });
  }

  // Register returning user status
  static registerReturningUserStatus(returningUserStatus: boolean): void {
    const status = returningUserStatus ? "TRUE" : "FALSE";
    mixpanel.register({
      "Returning User": status,
    });
  }

  // Methods to set session data
  static addSessionData(data: Record<string, unknown> = {}): void {
    MixpanelHelpers.sessionData = {
      ...MixpanelHelpers.sessionData,
      ...data,
    };

    // Include Session Id if available
    if (MixpanelHelpers.sessionId) {
      MixpanelHelpers.sessionData["Session Id"] = MixpanelHelpers.sessionId;
    }
  }

  static registerOrderAmount(orderAmount: number): void {
    MixpanelHelpers.addSessionData({
      "Order Amount": orderAmount,
    });
  }

  static registerStoreName(storeName: string): void {
    MixpanelHelpers.addSessionData({
      "Store Name": storeName,
    });
  }

  static registerUserAgent(): void {
    if (typeof navigator !== "undefined" && "userAgent" in navigator) {
      const userAgent = navigator.userAgent || "";
      MixpanelHelpers.addSessionData({
        "User Agent": userAgent,
      });
    }
  }

  static registerMerchantTracking(merchantTracking: string): void {
    MixpanelHelpers.addSessionData({
      "Merchant Tracking": merchantTracking,
    });
  }

  static registerPaymentId(paymentId: string): void {
    MixpanelHelpers.addSessionData({
      "Payment ID": paymentId,
    });
  }

  static registerPaymentStatus(status: string): void {
    MixpanelHelpers.addSessionData({
      "Payment Status": status,
    });
  }

  static registerViewportOrientation(): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewPortAspectRatio = Math.floor((viewportWidth / viewportHeight) * 100) / 100;
    const viewportOrientation = viewportHeight > viewportWidth ? "portrait" : "landscape";

    MixpanelHelpers.addSessionData({
      "Viewport Orientation": viewportOrientation,
      "Viewport Width": viewportWidth,
      "Viewport Height": viewportHeight,
      "Viewport AspectRatio": viewPortAspectRatio,
    });
  }

  // User profile functions
  static userProfileIdentify(trackingId: string): void {
    mixpanel.identify(trackingId);
    mixpanel.people.set({ "Consumer Hash": trackingId });
  }

  static userProfileUpdate(userInformation: Record<string, unknown>): void {
    mixpanel.people.set({ ...userInformation });
  }

  static recordRevenue(orderAmount: number): void {
    mixpanel.people.track_charge(orderAmount, { $time: new Date() });
  }

  // Track action events
  static trackAction({ pathname, customPath, actionName, actionItem, extraData }: TrackActionParams): void {
    const trackEvent = () => {
      const location = customPath || getScreenName(pathname) || pathname;
      const screenExtra = customPath ? undefined : getScreenExtra(pathname);
      const commonAction = COMMON_ACTION_SETS[actionName];
      const eventName = `${location} - ${commonAction?.actionName || actionName}`;
      const item = actionItem ? { item: actionItem } : undefined;

      const timeStamp = {
        "Time MS": Date.now(),
      };

      const eventData = {
        ...commonAction?.extra,
        ...screenExtra,
        ...extraData,
        ...item,
        ...MixpanelHelpers.sessionData,
        ...timeStamp,
      };

      mixpanel.track(eventName, eventData);
    };

    // If not initialized, queue the event
    if (!MixpanelHelpers.isInitialized) {
      MixpanelHelpers.eventQueue.push(trackEvent);
    } else {
      trackEvent();
    }
  }

  // Track duration events
  static trackDuration({
    pathname,
    customPath,
    actionName,
    actionItem,
    extraData,
    shouldEndTracker = false,
  }: TrackDurationParams): void {
    const trackDurationEvent = () => {
      const location = customPath || getScreenName(pathname) || pathname;
      const screenExtra = customPath ? undefined : getScreenExtra(pathname);
      const commonAction = COMMON_ACTION_SETS[actionName];
      const eventName = `${location} - ${commonAction?.actionName || actionName} - Duration`;
      const item = actionItem ? { item: actionItem } : undefined;

      const timeStamp = {
        "Time MS": Date.now(),
      };

      if (shouldEndTracker) {
        mixpanel.track(eventName, {
          ...commonAction?.extra,
          ...screenExtra,
          ...extraData,
          ...item,
          ...MixpanelHelpers.sessionData,
          ...timeStamp,
        });
      } else {
        mixpanel.time_event(eventName);
      }
    };

    // If not initialized, queue the event
    if (!MixpanelHelpers.isInitialized) {
      MixpanelHelpers.eventQueue.push(trackDurationEvent);
    } else {
      trackDurationEvent();
    }
  }
}
